<div :class="$style.descriptions" v-show="isSet()">
  <section class="mt-section">
    <v-grid in="10">
      <v-row
        v-if="submission.status === 'in_work' || submission.status === 'live'"
      >
        <v-col has="10">
          <fou-expand :id="$vId(this, 'expand')"
            rows="1" 
            :trigger-title="$t('pages.submissions.components.descriptions.description.read_more')"
            :trigger-title-open="$t('pages.submissions.components.descriptions.description.read_less')"
          >
            <p v-html="typefaceDescription"></p>
            <p
              :class="$style.mtLines"
              v-html="$sanitize($t('pages.submissions.components.descriptions.description.ideas.head'))"
            ></p>
            <ul class="mt-list mt-list--disc mt-list--indent">
              <li
                class="mt-list-item"
                v-html="$sanitize($t('pages.submissions.components.descriptions.description.ideas.suggest'))"
              ></li>
              <li
                class="mt-list-item"
                v-html="$sanitize($t('pages.submissions.components.descriptions.description.ideas.unique'))"
              ></li>
              <li
                class="mt-list-item"
                v-html="$sanitize($t('pages.submissions.components.descriptions.description.ideas.related'))"
              ></li>
              <li
                class="mt-list-item"
                v-html="$sanitize($t('pages.submissions.components.descriptions.description.ideas.functional'))"
              ></li>
            </ul>
            <p
              :class="$style.mtLines"
              v-html="$sanitize($t('pages.submissions.components.descriptions.description.options.head'))"
            ></p>
            <ul class="mt-list mt-list--disc mt-list--indent">
              <li
                class="mt-list-item"
                v-html="$sanitize($t('pages.submissions.components.descriptions.description.options.explain'))"
              ></li>
              <li
                class="mt-list-item"
                v-html="$sanitize($t('pages.submissions.components.descriptions.description.options.describe'))"
              ></li>
              <li
                class="mt-list-item"
                v-html="$sanitize($t('pages.submissions.components.descriptions.description.options.provide'))"
              ></li>
            </ul>
          </fou-expand>
        </v-col>
      </v-row>

      <v-row class="mt-group">
        <v-col has="10">
          <fou-group v-if="descriptions.length">
              <AiTextEditor
              :config="aiTextEditorConfig"
              :initialValue="descriptions[0].description"
              :validateOnMount="!this.submission.isLive || !descriptions[0].description"
              :foundryId="this.submission.foundry?.foundryId"
              :validationTriggerKey="validationTriggerKey"
              @on-change-html="handleChangeAiTextEditor"
              @on-blur-validation-done="handleBlurAiTextEditor"
              @on-banned-word-update="updateBannedWordStatus"
            />
          </fou-group>
        </v-col>
      </v-row>
    </v-grid>
  </section>

  <!-- add tags -->
  <section class="mt-section">
    <v-grid in="10">
      <!-- Header: always visible -->
      <v-row class="mt-line">
        <v-col has="9">
          <label
            :for="$vId(this, 'tags')"
            class="mt-serif--bold mt-label--black mt-label--size20"
          >
            {{$t('pages.submissions.components.descriptions.tags.heading')}}
          </label>
          <div class="mt-line">
            <div
              :class="$style.tagSection"
              v-html="tagDescription"
              @click="handleTagDescriptionClick($event)"
            ></div>
          </div>
        </v-col>
      </v-row>

      <!-- State 1: SFDMS Loading -->
      <template v-if="!isSfdmsComplete && binaryList.length > 0">
        <v-row>
          <v-col has="9">
            <div :class="$style.sfdmsLoadingContainer" data-qa-id="ai-tags-loading">
              <MdSpinner size="20px" color="blue-duck--500" />
              <span :class="$style.sfdmsLoadingText">Loading suggested tags for your font...</span>
            </div>
          </v-col>
        </v-row>
      </template>

      <!-- State 2 & 3: SFDMS Complete -->
      <template v-else>
        <!-- Tag input -->
        <v-row>
          <v-col has="9">
            <TextArea
              id="tags_descriptions"
              name="tags descriptions"
              v-model="enteredTag"
              @update:model-value="getCount"
              :input-class="[{ 'mt-input--error': Object.hasOwn(formError, 'tags descriptions') || inValidTag || bannedWordForTag || isValidTagLength || inValidChar || maxTagsReached || minTagLength}, $style.tagInputField]"
              :placeholder="$t('pages.submissions.components.descriptions.tags.placeholder')"
              :rules="enteredTag.length > 0 ? 'script_tag_validate' : tagRules"
              :handleEnterKeyDown="tagsPush"
              :handleEnterKeyUp="emptyField"
              :disabled="isInputDisabled()"
              :errorExist="Object.hasOwn(formError, 'tags descriptions') || inValidTag || bannedWordForTag || isValidTagLength || inValidChar || maxTagsReached || minTagLength"
              :rows="1"
              @blur="removeErrors"
            />
            <div :class="$style.errorMessageBlock">
              <p v-if="bannedWordForTag" class="ml-0 mt-error" :class="[$style.errorMessage, $style.spacingMargin]">{{$t('pages.profile.foundry_profile.banned_word_error_message')}}</p>
              <p v-if="isValidTagLength" class="mt-error">{{$t('pages.submissions.components.descriptions.tags.error_is_valid_length_tag')}}</p>
              <p v-if="minTagLength" class="mt-error">{{$t('pages.submissions.components.descriptions.tags.minTagLength')}}</p>
              <p v-if="inValidChar" class="mt-error">{{ $t('pages.submissions.components.descriptions.tags.special_char', { tags: specialCharacters.join('') }) }}</p>
              <p v-if="inValidTag" class="mt-error">{{$t('pages.submissions.components.descriptions.tags.validation_error')}}</p>
              <p v-if="maxTagsReached" class="mt-error">{{$t('pages.submissions.components.descriptions.tags.maxTagsReached')}}</p>
              <p v-if="isAtTagLimit()" :class="[$style.tagInfo, $style.tagLimitReached]">{{$t('pages.submissions.components.descriptions.tags.maxTagsReached')}}</p>
              <p v-if="!isAtTagLimit()" :class="$style.tagInfo" v-html="tagStyleInfo" @click="handleStyleInfoClick($event)"></p>
            </div>
          </v-col>
        </v-row>

        <!-- AI tags + User tags + Info bar / Suggest banner — all in one flex-wrap container (Figma node 1141:3530) -->
        <v-row>
          <v-col has="10">
            <div :class="$style.allTagsContainer" data-qa-id="all-tags-container">
              <!-- Info bar (full-width, sits on its own line in flex-wrap) -->
              <div v-if="hasAiTags" :class="$style.aiTagsInfoBar" data-qa-id="ai-tags-info-bar">
                <MdIconSmartStars size="16px" color="blue-duck--500" />
                <span :class="$style.aiTagsInfoText">These tags were generated by our system using common search terms to help customers find your fonts. Review the tags and make updates as needed.</span>
              </div>

              <!-- Suggest banner (full-width, sits on its own line) -->
              <div v-if="showSuggestBanner" :class="$style.suggestBanner" data-qa-id="ai-tags-suggest-banner">
                <div :class="$style.suggestBannerContent">
                  <span :class="$style.suggestBannerIcon">&#9432;</span>
                  <span :class="$style.suggestBannerText">Need help with tags? Let us suggest tags for your font.</span>
                  <button
                    :class="$style.suggestBannerLink"
                    @click="restoreAiTags"
                    type="button"
                    data-qa-id="ai-tags-restore-btn"
                  >Add suggested tags</button>
                </div>
                <button
                  :class="$style.suggestBannerClose"
                  @click="dismissSuggestBanner"
                  type="button"
                  aria-label="Dismiss suggestion"
                >
                  <MdIconClose size="16px" color="spirits--600" />
                </button>
              </div>

              <!-- AI tag pills -->
              <div
                v-for="(aiTag, index) in visibleAiTags"
                :key="'ai-tag-' + index"
                :class="$style.aiTagPill"
                :data-qa-id="'ai-tag-' + aiTag.name.toLowerCase().replace(/\s+/g, '-')"
              >
                <svg :class="$style.aiTagPillIcon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M0.260417 10.9583C0.104167 11.0174 0 11.1667 0 11.3333C0 11.5 0.104167 11.6493 0.260417 11.7083L2.22222 12.4444L2.95833 14.4062C3.01736 14.5625 3.16667 14.6667 3.33333 14.6667C3.5 14.6667 3.64931 14.5625 3.70833 14.4062L4.44444 12.4444L6.40625 11.7083C6.5625 11.6493 6.66667 11.5 6.66667 11.3333C6.66667 11.1667 6.5625 11.0174 6.40625 10.9583L4.44444 10.2222L3.70833 8.26042C3.64931 8.10417 3.5 8 3.33333 8C3.16667 8 3.01736 8.10417 2.95833 8.26042L2.22222 10.2222L0.260417 10.9583Z" fill="var(--md-color--spirits--500)"/>
                  <path d="M5.74998 6.06659C5.49998 6.16103 5.33331 6.39992 5.33331 6.66659C5.33331 6.93325 5.49998 7.17214 5.74998 7.26659L8.88887 8.44436L10.0666 11.5833C10.1611 11.8333 10.4 11.9999 10.6666 11.9999C10.9333 11.9999 11.1722 11.8333 11.2666 11.5833L12.4444 8.44436L15.5833 7.26659C15.8333 7.17214 16 6.93325 16 6.66659C16 6.39992 15.8333 6.16103 15.5833 6.06659L12.4444 4.88881L11.2666 1.74992C11.1722 1.49992 10.9333 1.33325 10.6666 1.33325C10.4 1.33325 10.1611 1.49992 10.0666 1.74992L8.88887 4.88881L5.74998 6.06659Z" fill="var(--md-color--spirits--500)"/>
                </svg>
                <span :class="$style.aiTagPillText">{{ aiTag.name }}</span>
                <button
                  :class="$style.aiTagPillClose"
                  @click="removeAiTag(aiTag.name)"
                  type="button"
                  :aria-label="'Remove tag ' + aiTag.name"
                >
                  <MdIconClose size="16px" color="spirits--600" />
                </button>
              </div>

              <!-- User tag pills -->
              <fou-select-tags
                :id="$vId(this, 'tags-selection')"
                :fou-select="tags"
                :warning-words="warningWords"
                :fou-select-id="$vId(this, 'tags')"
                @on:remove="deselectFromTag"
                :class="$style.createdTags"
                variant="outlined"
              >
              </fou-select-tags>
            </div>
          </v-col>
          <v-col has="10">
            <p v-if="warningWordForTag && warningWords.length>0" class="ml-0 mt-error" :class="[$style.warningWordErrorMessage, $style.errorMessage]">{{$t('pages.profile.foundry_profile.warning_word_error_message_tags')}}</p>
          </v-col>
        </v-row>

        <div v-if="isOverTagLimit()">
          <p
            class="mt-error mt-10"
            :class="submission.status === 'live' ? [$style.warningWordErrorMessage, $style.errorMessage] : ''"
          >{{$t('pages.submissions.components.descriptions.tags.tagLimitExceeded')}}</p>
        </div>
      </template>

      <!-- Tag actions: counter + clear all -->
      <v-row>
        <v-col has="10">
          <div :class="$style.tagActions">
            <span :class="$style.tagCountBadge" data-qa-id="ai-tags-count">{{ totalTagCount }}/{{ tagsLength }} tags</span>
            <MdButton
              size="small"
              variant="bordered"
              color="blue"
              type="button"
              :disabled="totalTagCount === 0"
              @click="clearAllTags"
              data-qa-id="clear-all-tags-btn"
            >Clear all tags</MdButton>
          </div>
        </v-col>
      </v-row>

      <!-- Feedback section (UI only, non-functional) -->
      <v-row v-if="hasAiTags && isFeedbackVisible">
        <v-col has="9">
          <div :class="$style.feedbackCard" data-qa-id="ai-tags-feedback">
            <div :class="$style.feedbackHeader">
              <span :class="$style.feedbackTitle">Share your feedback</span>
              <button
                :class="$style.feedbackClose"
                @click="dismissFeedback"
                type="button"
                aria-label="Close feedback"
              >
                <MdIconClose size="16px" color="spirits--600" />
              </button>
            </div>
            <textarea
              :class="$style.feedbackTextarea"
              v-model="feedbackText"
              placeholder="Help us improve these tags. What fits and what doesn't?"
              :maxlength="feedbackCharLimit"
              rows="3"
            ></textarea>
            <div :class="$style.feedbackFooter">
              <span :class="$style.feedbackCharCount">{{ feedbackText.length }}/{{ feedbackCharLimit }}</span>
            </div>
            <MdButton
              size="small"
              variant="none"
              color="blue"
              type="button"
              :disabled="!feedbackText.trim()"
              @click="submitFeedback"
              data-qa-id="ai-tags-feedback-submit"
            >Submit feedback</MdButton>
          </div>
        </v-col>
      </v-row>
    </v-grid>
  </section>

  <section class="mt-section" v-if="languagesFeature">
    <v-grid in="10">
      <v-row class="mt-line">
        <v-col has="10">
          <p v-html="$sanitize($t('pages.submissions.components.descriptions.locales.description'))"></p>
        </v-col>
      </v-row>
      <v-row class="mt-line">
        <v-col has="5">
          <label :for="$vId(this, 'add-locale')" class="mt-label">
            {{$t('pages.submissions.components.descriptions.locales.heading')}}
          </label>
          <fou-select multiple tags-outside :id="$vId(this, 'add-locale')" name="add_locale"
            :value.sync="locales.values" :options="locales.options" :on-change="updateLocalesValues" type="checkbox"
            className="mt-checkbox" :placeholder="$t('pages.submissions.components.descriptions.locales.placeholder')">
          </fou-select>
        </v-col>
      </v-row>
      <v-row class="mt-group">
        <v-col has="10">
          <fou-select-tags :id="$vId(this, 'add-locale-tags')" :fou-select="locales"
            :fou-select-id="$vId(this, 'add-locale')">
          </fou-select-tags>
        </v-col>
      </v-row>
      <fou-group :key="key" v-if="hasLocalizations() && key !== 0" v-for="d, key in descriptions">
        <v-grid in="10">
          <v-row>
            <v-col has="7">
              <span class="mt-label">{{d.localization}}</span>
            </v-col>
            <v-col has="2" off="1" class="mt-flex mt-flex--end">
              <button :id="$vId(this, 'remove-description-' + d.language)" class="mt-icon mt-icon-close"
                :class="$style.controlButton" @click="removeLocalization(key - 1)">
              </button>
            </v-col>
          </v-row>
          <textarea :id="$vId(this, 'description-' + d.language)" :name="`description_${d.language}`"
            class="mt-textarea"
            :class="[{'mt-textarea--error': errors.has(`description_${d.language}`)}, $style.textareaHeight]"
            v-model="d.description"
            :placeholder="$t('pages.submissions.components.descriptions.locales.textarea.placeholder')"
            :data-vv-as="`${$t('pages.submissions.components.descriptions.locales.textarea.name')} ${d.localization}`"
            maxlength="50000" v-validate="'required|max:50000'" @blur="commitChange()">
          </textarea>
          <fou-group v-if="errors.has(`description_${d.language}`)">
            <p class="mt-error" v-for="error in errors.collect(`description_${d.language}`)">{{error}}</p>
          </fou-group>
        </v-grid>
      </fou-group>
    </v-grid>
  </section>

  <!-- add related family -->
  <section class="mt-section related-families" v-if="getSelectedFoundry && getSelectedFoundry.contractSummary === 'UFDA 2'">
    <v-grid in="10">
      <v-row class="mt-line">
        <v-col has="9">
          <label :for="$vId(this, 'tags')" class="mt-serif--bold mt-label--black mt-label--size20">
            {{$t('pages.submissions.components.descriptions.relatedFamilies.heading')}}
          </label>
          <div class="mt-line">
            <p :class="$style.tagSection"
              v-html="relatedFamiliesDescription">
            </p>
          </div>
        </v-col>
        <v-col has="5">
          <fou-select
             ref="familiesSelect"
              multiple
              tags-outside
              deselect-from-dropdown
              name="families descriptions"
              :class="[$style.inputFont, $style.inputWidth2]"
              :id="$vId(this, 'families')"
              :options="families.options"
              :model-value="families.values"
              :reduce="op=>op"
              :hide-drop="familiesHideDrop"
              :split-tags-len="familiesLength"
              @search="getFamiliesOptions"
              @search:blur="onSearchBlurFamilies"
              @option:selected="checkUpdateFamiliesValues"
              @option:deselected="optionDeselectedDD"
              :on-delete="familiesDeleteLengthError"
              :close-on-select="false"
              :placeholder="$t('pages.submissions.components.descriptions.relatedFamilies.placeholder')"
              :see="'na'"
              type="checkbox"
              class-name="mt-checkbox"
            />
        </v-col>
      </v-row>
      <v-row>
        <v-col has="10" :class="$style.mT6">
          <fou-select-families @on:remove="deselectFromFamilies" :id="$vId(this, 'families-selection')" :fou-select="families" :fou-select-id="$vId(this, 'families')" ref="familySelect">
          </fou-select-families>
        </v-col>
      </v-row>
    </v-grid>
    <fou-modal
      v-if="isFamilySiblingModalVisible"
      :visible="isFamilySiblingModalVisible"
      cols="8">
      <template #content>
        <div :class="$style.slotContent">
          <h3 class="mt-h3" :class="$style.modalTitle">{{$t('pages.submissions.components.descriptions.add_familiy_sibling_modal.title', {familyName: selectedFamily.label, siblingCount: siblings.length})}}</h3>
          <fou-group bottom="medium">
            <p :class="$style.modalDescription">{{$t('pages.submissions.components.descriptions.add_familiy_sibling_modal.subtitle')}}</p>
          </fou-group>
          <div class="mt-h4">
            <h4 :class="$style.modalDescription">{{siblingsText}}</h4>
          </div>
          <fou-group class="mt-flex mt-flex--between" :class="$style.modalControls">
            <button
              type="button"
              class="mt-button mt-button--inverted"
              :class="$style.modalControlsItem"
              @click="cancelModal"
            >
              {{$t('pages.submissions.components.descriptions.add_familiy_sibling_modal.cancel')}}
            </button>
            <button
              type="button"
              class="mt-button"
              :class="$style.modalControlsItem"
              @click="updateFamiliesValues()"
            >
              {{$t('pages.submissions.components.descriptions.add_familiy_sibling_modal.confirm')}}
            </button>
          </fou-group>
        </div>
      </template>
    </fou-modal>
  </section>
</div>